import { useTranslation } from '../../i18n';
import { C } from '../../constants/theme';
import { soft } from '@parsina/framework/tokens';
import { usePatientReferences } from '../../hooks/usePatientReferences';
import {
  findDuplicateReference, isCheckedMethod, normalizeReference, MIN_REFERENCE_LENGTH,
} from '../../lib/referenceDuplicates';

/**
 * «این شمارهٔ پیگیری قبلاً ثبت شده است» — under the reference field of a bank transfer
 * (PM-0711).
 *
 * The same transfer entered twice (as the invoice's payment and as an appointment's
 * deposit) made the account claim money that arrived once, and nothing said so. This
 * says so, with WHAT was recorded, how much and when. It is a notice, never a refusal:
 * one transfer can honestly be split across several entries, and the desk knows.
 *
 * Silent in every other case — no patient, another method, a half-typed number, nothing
 * recorded under it, or a read that failed.
 */
export default function DuplicateReferenceHint({ patientId, method, reference, style }) {
  const { t, formatMoney, moneyUnit, formatDateTime } = useTranslation();
  const armed = !!patientId && isCheckedMethod(method)
    && normalizeReference(reference).length >= MIN_REFERENCE_LENGTH;
  const known = usePatientReferences(patientId, armed);
  const hit = armed ? findDuplicateReference({ method, reference, ...known }) : null;
  if (!hit) return null;

  const at = Number(hit.at);
  return (
    <div
      role="status"
      data-testid="reference-duplicate-hint"
      style={{
        padding: '8px 11px', borderRadius: 8, fontSize: '0.71875rem', lineHeight: 1.7,
        background: soft(C.amber, 0.12), border: `1px solid ${soft(C.amber, 0.45)}`,
        color: C.textPrimary, ...style,
      }}
    >
      {t('accounting.duplicateReference.message', {
        what: t(`accounting.duplicateReference.kind.${hit.kind}`),
        amount: `${formatMoney(hit.amountRial, { currency: hit.currency })} ${moneyUnit('short', hit.currency)}`,
        when: Number.isFinite(at) && at > 0 ? formatDateTime(at, 'short') : '—',
      })}
    </div>
  );
}
